import {Textarea} from '@mantine/core';
import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import React, {useState} from 'react';
import type {DeficiencyResponse} from 'src/api/ProactApiClient';
import {DeficiencyStatusEnum} from 'src/api/ProactApiClient';
import {DeficiencyCard} from 'src/app/inspections/DeficiencyCard';
import {Button} from 'src/design/atoms/Button';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {
  useGetInspectionDeficiencyListQuery,
  useResolveDeficiencyMutation,
} from 'src/redux/queries/deficiency';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface DeficienciesTabProps {
  inspectionId: number;
  isCompleted: boolean;
}

const PAGE_LENGTH = 500;

/** Per-inspection deficiencies tab — Open/Resolved pills, with Resolve for open items. */
export const DeficienciesTab: React.FC<DeficienciesTabProps> = ({
  inspectionId,
  isCompleted,
}) => {
  const {setAlert} = useAlert();
  const perms = useInspectionPermissions();
  const [resolveTarget, setResolveTarget] = useState<DeficiencyResponse>();
  const [resolveNote, setResolveNote] = useState('');
  const [confirmOpened, confirmHandlers] = useDisclosure(false);
  const {data, isFetching} = useGetInspectionDeficiencyListQuery({
    id: inspectionId,
    inspectionId,
    start: 0,
    length: PAGE_LENGTH,
  });
  const [resolveDeficiency, {isLoading: isResolving}] =
    useResolveDeficiencyMutation();

  const items = data?.data ?? [];

  const handleResolve = async () => {
    if (!resolveTarget) return;

    try {
      await resolveDeficiency({
        id: resolveTarget.id,
        request: {note: resolveNote || undefined},
      }).unwrap();
      setAlert('Deficiency resolved.', 'success');
      confirmHandlers.close();
      setResolveTarget(undefined);
      setResolveNote('');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  if (isFetching) {
    return (
      <Center py="lg">
        <Loader size="sm" />
      </Center>
    );
  }

  if (items.length === 0) {
    return (
      <NoItemsPanel
        noItemsText={
          isCompleted
            ? 'No deficiencies recorded for this inspection.'
            : 'Deficiencies are generated when the inspection completes.'
        }
      />
    );
  }

  return (
    <Stack gap="sm">
      <InfoAlert mb="xs">
        Deficiencies are auto-generated from failed devices.
      </InfoAlert>

      <Text size="sm" c="dimmed">
        {items.length} deficienc{items.length === 1 ? 'y' : 'ies'} ·
        auto-generated from failed devices
      </Text>

      {items.map(deficiency => (
        <DeficiencyCard
          key={deficiency.id}
          deficiency={deficiency}
          action={
            perms.resolveDeficiency &&
            deficiency.status === DeficiencyStatusEnum.Open ? (
              <Button
                size="xs"
                variant="subtle"
                onClick={() => {
                  setResolveTarget(deficiency);
                  confirmHandlers.open();
                }}>
                Resolve
              </Button>
            ) : undefined
          }
        />
      ))}

      <ConfirmDialog
        opened={confirmOpened}
        onClose={() => {
          confirmHandlers.close();
          setResolveTarget(undefined);
          setResolveNote('');
        }}
        onConfirm={handleResolve}
        confirmationButtonColor="green"
        confirmationButtonDisabled={isResolving}
        title="Resolve this deficiency?"
        body={
          <Textarea
            label="Resolution note (optional)"
            placeholder="Add a note"
            value={resolveNote}
            onChange={event => setResolveNote(event.currentTarget.value)}
            autosize
            minRows={2}
          />
        }
      />
    </Stack>
  );
};
